<div *ngIf="isLoading" class="flex items-center justify-center p-8">
  <div class="text-center">
    <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
    <p class="text-sm text-gray-600">Loading form...</p>
  </div>
</div>

<form [formGroup]="formGroup" class="tax-form" *ngIf="!isLoading">
  <div class="form-row" [style.grid-template-columns]="getGridTemplateColumns()">
    <ng-container *ngFor="let field of dynamicFormFields; let i = index">
      <div class="form-field" [style.gridColumn]="'span ' + getColSpan(field)" *ngIf="shouldShowField(field)">
        <label class="font-semibold text-sm mb-2" [for]="getFieldKey(field.field_name)">
          {{ field.display_name }}
          <span *ngIf="field.is_mandatory" class="text-red-500">*</span>
        </label>

          <!-- VIEW MODE: Display values as text -->
          
          <!-- File upload view mode -->
          <div *ngIf="(viewMode || field.view_only) && field.field_type === 'file_upload'" class="text-sm rounded min-h-[12px] flex items-center">
            <ng-container *ngIf="hasFileValue(field)">
              <a class="text-sm text-primary underline cursor-pointer file-name-view"
                 [title]="getSelectedFileName(field)"
                 (click)="openDownloadUrl(field); $event.preventDefault()">
                {{ getSelectedFileName(field) }}
              </a>
            </ng-container>
            <span *ngIf="!hasFileValue(field)" class="text-sm text-gray-400">-</span>
          </div>

          <!-- Password field view mode - always masked -->
          <div *ngIf="viewMode && field.field_type === 'password'" class="text-sm rounded min-h-[12px] flex items-center">
            <span class="text-gray-400">••••••••</span>
          </div>

          <!-- Other fields view mode -->
          <div *ngIf="(viewMode || field.field_type === 'label') && field.field_type !== 'file_upload' && field.field_type !== 'password' && field.field_type !== 'button'" class="text-sm rounded min-h-[12px] flex items-center">
            {{ getFieldDisplayValue(field)}}
          </div>

          <!-- Button field (shown in both view and edit modes) -->
          <p-button *ngIf="field.field_type === 'button'"
            type="button"
            [label]="field.display_name"
            [icon]="field.icon || ''"
            [styleClass]="field.button_style || 'p-button-outlined'"
            (onClick)="onButtonClick(field)" />

        <!-- EDIT MODE: Input controls -->
        <ng-container *ngIf="!viewMode && field.field_type !== 'label' && !(field.view_only && field.field_type === 'file_upload')">
          <!-- Readonly field display -->
          <span *ngIf="field.field_type === 'label'" class="inline-block w-full text-sm text-gray-700">
            {{ getFieldDisplayValue(field) }}
          </span>

          <!-- Text Input -->
          <input *ngIf="field.field_type === 'input'" pInputText type="text" class="input small-placeholder"
            placeholder="Enter {{ field.display_name }}" [formControlName]="getFieldKey(field.field_name)"
            [id]="getFieldKey(field.field_name)"
            (input)="field.numeric_only ? stripNonDigits($event, field) : onFieldChange(field.field_name, $event)"
            [attr.inputmode]="field.numeric_only ? 'numeric' : null"
            (keypress)="field.numeric_only ? onlyNumbers($event) : null" />

          <!-- Password Input -->
          <input *ngIf="field.field_type === 'password'" pInputText type="password" class="input small-placeholder"
            placeholder="Enter {{ field.display_name }}" [formControlName]="getFieldKey(field.field_name)"
            [id]="getFieldKey(field.field_name)" (input)="onFieldChange(field.field_name, $event)" />


          <!-- Dropdown -->
          <p-select *ngIf="field.field_type === 'select'" class="p-select" size="small" fluid [options]="field.options"
            [id]="getFieldKey(field.field_name)" [formControlName]="getFieldKey(field.field_name)"
            placeholder="Select {{ field.display_name }}" optionLabel="label" optionValue="value" [appendTo]="'body'"
            [showClear]="!field.is_mandatory" [loading]="field.loading || false"
            (onChange)="onFieldChange(field.field_name, $event, field)" [filter]="true"
            (onShow)="onShow(i)"
            (onHide)="onHide(i)"
            (onFilter)="cacheFilter(i)"
          >
            <ng-template #dropdownicon>
              <i class="pi pi-sort-down-fill" style="font-size: 0.7rem"></i>
            </ng-template>
          </p-select>

          <!-- Month-Year Datepicker -->
          <p-datepicker *ngIf="field.field_type === 'month_year'" fluid size="small"
            [id]="getFieldKey(field.field_name)" [formControlName]="getFieldKey(field.field_name)" view="month"
            dateFormat="MM yy" [showIcon]="true" [readonlyInput]="false" [appendTo]="'body'" class="w-full"
            (onSelect)="onMonthYearChange(field.field_name, $event)"></p-datepicker>

          <!-- Multi Select -->
          <p-multiselect *ngIf="field.field_type === 'multiselect'" size="small" [options]="field.options"
            [id]="getFieldKey(field.field_name)" [formControlName]="getFieldKey(field.field_name)"
            [placeholder]="'Select ' + field.display_name" optionLabel="label" optionValue="value" [appendTo]="'body'"
            filter="false" [display]="field.display || 'chip'"
            (onChange)="onFieldChange(field.field_name, $event, field)" />

          <!-- Date -->
          <p-datepicker *ngIf="field.field_type === 'date'" fluid size="small" [id]="getFieldKey(field.field_name)"
            [formControlName]="getFieldKey(field.field_name)" dateFormat="dd/mm/yy" [defaultDate]="field.default_value"
            [showIcon]="true" [showClear]="!field.is_mandatory" [timeOnly]="false" [showTime]="field.showTime || false" [minDate]="field.min_date || null"
            [maxDate]="field.max_date || null" [appendTo]="'body'" class="w-full"
            (onSelect)="onFieldChange(field.field_name, $event)"
            (onClear)="onFieldChange(field.field_name, null)"></p-datepicker>

          <!-- Time -->
          <p-datepicker *ngIf="field.field_type === 'datetime'" fluid size="small" [id]="getFieldKey(field.field_name)"
            [formControlName]="getFieldKey(field.field_name)" [timeOnly]="true" [hourFormat]="'24'" [showIcon]="true"
            [appendTo]="'body'" class="w-full" (onSelect)="onFieldChange(field.field_name, $event)"></p-datepicker>

          <!-- Checkbox -->
          <div *ngIf="field.field_type === 'checkbox'" class="checkbox-single">
            <input type="checkbox" class="checkbox" [formControlName]="getFieldKey(field.field_name)"
              [id]="getFieldKey(field.field_name)" (change)="onFieldChange(field.field_name, $event)" />
          </div>

          <!-- textarea -->
          <div *ngIf="field.field_type === 'textarea'" class="input-textarea-container">
            <textarea rows="1" pTextarea pSize="small" class="w-full h-full"
              [formControlName]="getFieldKey(field.field_name)" [id]="getFieldKey(field.field_name)"
              (input)="onFieldChange(field.field_name, $event)"></textarea>
          </div>

            <!-- file-upload -->
            <div *ngIf="field.field_type === 'file_upload'" class="file-upload-container">
              <input
                type="file"
                class="file-input"
                [id]="getFieldKey(field.field_name)"
                [accept]="field.accept || '*'"
                [multiple]="field.multiple || false"
                (change)="onFileSelected($event, field)"
                #fileInput
              />
              <!-- Show Choose File button only when no file is selected -->
              <label *ngIf="!getSelectedFileName(field)" [for]="getFieldKey(field.field_name)" class="file-upload-btn">
                <i class="pi pi-upload"></i>
                <span>{{ field.upload_label || 'Choose File' }}</span>
              </label>
              <!-- Show file name with download link when file is selected -->
              <div *ngIf="getSelectedFileName(field)" class="file-info">
                <a class="text-sm text-primary underline cursor-pointer file-name"
                   [title]="getSelectedFileName(field)"
                   (click)="openDownloadUrl(field); $event.preventDefault()">
                  {{ getSelectedFileName(field) }}
                </a>
                <button type="button" class="file-remove-btn" (click)="removeFile(field, fileInput)">
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>

          <!-- number -->
          <p-inputnumber *ngIf="field.field_type === 'number'" size="small" [suffix]="field.suffix || ''"
            [prefix]="field.prefix || ''" fluid [formControlName]="getFieldKey(field.field_name)"
            [id]="getFieldKey(field.field_name)" (onInput)="onFieldChange(field.field_name, $event)"
            placeholder="Enter {{ field.display_name }}" />

          <!-- double (decimal number) -->
          <p-inputnumber *ngIf="field.field_type === 'double'" size="small" [suffix]="field.suffix || ''"
            [prefix]="field.prefix || ''" fluid [formControlName]="getFieldKey(field.field_name)"
            [id]="getFieldKey(field.field_name)" (onInput)="onFieldChange(field.field_name, $event)"
            placeholder="Enter {{ field.display_name }}" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="field.maxFractionDigits || 2" />

          <!-- readonly -->
          <input *ngIf="field.field_type === 'readonly'" pInputText type="text" class="input"
            [formControlName]="getFieldKey(field.field_name)" [id]="getFieldKey(field.field_name)" [readonly]="true" />

          <!-- radio -->
          <div class="flex flex-col gap-2" *ngIf="field.field_type === 'radio'">

            <div class="flex gap-6">
              <label *ngFor="let option of field.options" class="flex items-center gap-2">
                <input type="radio" [value]="option.value" [formControlName]="getFieldKey(field.field_name)"
                  [id]="getFieldKey(field.field_name) + '_' + option.label"
                  (change)="onFieldChange(field.field_name, option.value)" />
                {{ option.label}}
              </label>
            </div>

          </div>

          <!-- radio_boolean (boolean Yes/No radio) -->
          <div class="flex flex-col gap-2" *ngIf="field.field_type === 'radio_boolean'">
            <div class="flex gap-6">
              <label *ngFor="let option of field.options" class="flex items-center gap-2">
                <input type="radio" [value]="option.value" [formControlName]="getFieldKey(field.field_name)"
                  [id]="getFieldKey(field.field_name) + '_' + option.label"
                  (change)="onFieldChange(field.field_name, option.value)" />
                {{ option.label }}
              </label>
            </div>
          </div>

          <!-- auto-complete -->
          <p-autocomplete *ngIf="field.field_type === 'autocomplete'" size="small" fluid
            [formControlName]="getFieldKey(field.field_name)" [id]="getFieldKey(field.field_name)"
            [suggestions]="field.options || []" [dropdown]="true" [forceSelection]="field.forceSelection !== false"
            [optionLabel]="field.autocompleteDisplayField || 'label'"
            (completeMethod)="onAutocompleteComplete($event, field)"
            (onSelect)="onFieldChange(field.field_name, $event)" (keydown)="onAutocompleteKeydown($event, field)"
            class="filter-autocomplete">
          </p-autocomplete>

          <!-- Color Picker -->
          <div *ngIf="field.field_type === 'color'" class="flex items-center gap-2">
            <p-colorpicker [formControlName]="getFieldKey(field.field_name)"
              [id]="getFieldKey(field.field_name)"
              (onChange)="onFieldChange(field.field_name, $event)">
            </p-colorpicker>
            <span class="text-sm text-gray-500">{{ formGroup.get(getFieldKey(field.field_name))?.value || '' }}</span>
          </div>



          <!-- Regex validation error (small red line) -->
          <div *ngIf="regexValidationErrors && regexValidationErrors[getFieldKey(field.field_name)]"
            class="mt-0 text-[11px] leading-snug text-red-500">
            {{ regexValidationErrors[getFieldKey(field.field_name)] }}
          </div>

        </ng-container>

      </div>
    </ng-container>
  </div>
</form>